import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { Alert, Spinner } from 'elmo-elements';
import { useErrorHandler } from 'react-error-boundary';

import config from '@/lib/config/index';
import { appRoutes } from '@/app/routes';
import { useGetCapabilitiesQuery, useGetTokenQuery } from '@/modules/services';

import './App.scss';
import NestedRoutesWithMenu from './lib/route/NestedRoutes';
import {
  useGetCkfinderJwtQuery,
  useGetContentSignedCookiesQuery,
} from '@/modules/services/auth';
import { apm } from '@elastic/apm-rum';
import * as pendo from '@/lib/pendo';
import { User } from '@/modules/types/user';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import ElaPermissions from '@/lib/auth/permissions/permission.type';

// Both cookies outlive this by design; the poll exists so a long session never
// reaches expiry, not to match it.
const COOKIE_POLL_INTERVAL_MS = 1000 * 60 * 10;

// Main app component with route detection logic
const AppContent = () => {
  const location = useLocation();
  const isScorm = location.pathname.startsWith('/scorm/course/');
  const isBreathe = location.pathname.startsWith('/breathe/');
  const isAnonymous = isScorm || isBreathe;

  // Execute auth-related queries only when not on anonymous routes
  const authToken = useGetTokenQuery(undefined, {
    skip: isAnonymous,
  });

  const capabilities = useGetCapabilitiesQuery('', {
    skip: isAnonymous || authToken.isFetching,
  });

  // Only check capabilities for non-anonymous routes
  const canEdit = useUserHasCapabilities([ElaPermissions.EDIT_ELEARNING], {
    skip: isAnonymous,
  });

  // Poll CKFinder JWT only for non-anonymous routes and users with edit permissions
  useGetCkfinderJwtQuery('', {
    pollingInterval: COOKIE_POLL_INTERVAL_MS,
    skip: isAnonymous || capabilities.isFetching || !canEdit,
  });

  // Anyone who can open the course builder needs to read /content/ assets,
  // matching the permissions the endpoint itself accepts.
  const canUseCourseBuilder = useUserHasCapabilities(
    [
      ElaPermissions.VIEW_ELEARNING,
      ElaPermissions.EDIT_ELEARNING,
      ElaPermissions.PUBLISH_REVIEW,
    ],
    { skip: isAnonymous },
  );

  // useUserHasCapabilities resolves in an effect, so it is undefined for one
  // render after capabilities land. Treated as "still deciding" below, otherwise
  // the routes would paint and request assets before the cookie query starts.
  // Requires the data too: without it the hook's effect returns early and never
  // resolves, so blocking on undefined alone would hang the app forever.
  const isCapabilityPending =
    canUseCourseBuilder === undefined && Boolean(capabilities.data);

  // Re-issued well inside its 24h lifetime so a long authoring session never
  // hits an expired cookie: browser-initiated image, CSS and video loads have
  // no retry path, they would simply render broken.
  const contentCookies = useGetContentSignedCookiesQuery('', {
    pollingInterval: COOKIE_POLL_INTERVAL_MS,
    skip: isAnonymous || capabilities.isFetching || !canUseCourseBuilder,
  });

  // Nothing recovers a 403'd image, so a failure here is invisible to the user
  // and to support unless it is reported.
  useEffect(() => {
    if (!contentCookies.isError) return;

    const { error } = contentCookies;
    // eslint-disable-next-line no-console
    console.error('[LMS2-4987] failed to obtain content access cookies', error);
    try {
      apm.captureError(
        error instanceof Error
          ? error
          : new Error(`Content signed-cookie request failed: ${JSON.stringify(error)}`),
      );
    } catch {
      // already logged to the console above
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentCookies.isError, contentCookies.error]);

  const handleError = useErrorHandler();

  useEffect(() => {
    // Initialize Pendo only for non-anonymous routes
    if (!isAnonymous && capabilities.currentData) {
      const urlParts = window.location.host.split('.');
      let companySubdomain = '';
      if (urlParts.length > 1) {
        [companySubdomain] = urlParts;
      }

      pendo.init(companySubdomain, capabilities.currentData as User);
    }
  }, [capabilities.currentData, isAnonymous]);

  // Anonymous routes render directly, skipping all auth logic
  if (isAnonymous) {
    return <NestedRoutesWithMenu routes={appRoutes} />;
  }

  // Authentication logic for non-SCORM routes
  if (config.api.enableBFF) {
    if (authToken.isLoading || capabilities.isLoading) {
      return <div className="loading" />;
    }

    if (authToken.isError) {
      handleError(authToken.error);
      return <Alert message="Error" type="danger" isCloseable={false} />;
    }
  }

  if (capabilities.isError) {
    handleError(capabilities.error);
    return <Alert message="Error" type="danger" isCloseable={false} />;
  }

  // Waiting on the content cookie too, so no /content/ request is made before it
  // lands, including the render where the capability check has not resolved yet.
  // Only the initial fetch blocks: an error resolves isLoading, so a failure
  // degrades to broken images rather than an unusable app.
  if (
    capabilities.isLoading ||
    authToken.isLoading ||
    isCapabilityPending ||
    contentCookies.isLoading
  )
    return <Spinner />;

  return <NestedRoutesWithMenu routes={appRoutes} />;
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
